import { useCallback, useMemo, useState } from 'react'
import type { FlowMeta } from '../types/flow'
import { loadFlowSessionToken, saveFlowSessionToken } from '../lib/flowApi'

export function useGuidedFlow() {
  const [flowMeta, setFlowMeta] = useState<FlowMeta | null>(null)
  const [flowSessionToken, setFlowSessionToken] = useState<string | null>(null)

  const applyStreamPayload = useCallback((data: {
    guided_flow?: FlowMeta | null
    flow_session_token?: string | null
    conversation_id?: number | null
  }) => {
    if (data.guided_flow) {
      setFlowMeta(data.guided_flow)
    }
    if (data.flow_session_token) {
      setFlowSessionToken(data.flow_session_token)
      if (data.conversation_id) {
        saveFlowSessionToken(data.conversation_id, data.flow_session_token)
      }
    }
  }, [])

  const restoreTokenForConversation = useCallback((conversationId: number) => {
    const stored = loadFlowSessionToken(conversationId)
    if (stored) setFlowSessionToken(stored)
    return stored
  }, [])

  const hydrateFromStatus = useCallback((data: {
    guided_flow: FlowMeta
    flow_session_token?: string | null
    conversation_id: number
  }) => {
    setFlowMeta(data.guided_flow)
    if (data.flow_session_token) {
      setFlowSessionToken(data.flow_session_token)
      saveFlowSessionToken(data.conversation_id, data.flow_session_token)
    }
  }, [])

  const resetFlow = useCallback(() => {
    setFlowMeta(null)
    setFlowSessionToken(null)
  }, [])

  const progressLabel = useMemo(() => {
    if (!flowMeta) return null
    if (flowMeta.phase === 'questions') {
      if (flowMeta.current_step_id === 'intro') return 'Introduction'
      if (flowMeta.question_number) {
        return `Q${flowMeta.question_number}/${flowMeta.question_total}`
      }
      return `Questions · ${flowMeta.question_total}`
    }
    if (flowMeta.phase === 'deliverables' || flowMeta.phase === 'complete') {
      const done = flowMeta.deliverables_done?.length ?? 0
      return `${done}/${flowMeta.deliverable_total}`
    }
    return null
  }, [flowMeta])

  const showDeliverablesButton =
    (flowMeta?.deliverables_done?.length ?? 0) > 0
    || flowMeta?.phase === 'deliverables'
    || flowMeta?.phase === 'complete'

  const isGuidedActive = !!flowMeta

  return {
    flowMeta,
    flowSessionToken,
    applyStreamPayload,
    restoreTokenForConversation,
    hydrateFromStatus,
    resetFlow,
    progressLabel,
    showDeliverablesButton,
    isGuidedActive,
  }
}
